import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { atGetRequestDetail, atCancelRequest } from "@/lib/api";

const NAVY = "#0A1628";
const GOLD = "#D4AF37";
const GREEN = "#1A7A4A";
const RED = "#A32D2D";

const STATUS_COLOR = { open: GREEN, filled: NAVY, cancelled: "#5b5e69", expired: "#9ca3af" };
const STATE_COLOR = {
  queued: "#5b5e69", notified_sms: NAVY, notified_email: NAVY, notified_voice: "#8a4b00",
  accepted: GREEN, declined: RED, timed_out: "#9ca3af", cancelled: "#9ca3af",
};

/** Contractor's view of a single labor request + live matches with score bars. */
export default function AvailableTodayRequestDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await atGetRequestDetail(id);
      setData(r);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Poll every 8s so the contractor sees SMS/voice/accept states update live.
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [id]);

  const cancel = async () => {
    if (!window.confirm("Cancel this request? All pending workers will be notified.")) return;
    setCancelling(true);
    try {
      await atCancelRequest(id);
      load();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Cancel failed.");
    } finally {
      setCancelling(false);
    }
  };

  if (loading && !data) return <div style={{ padding: 60, textAlign: "center", color: "#5b5e69" }}>Loading…</div>;
  if (error) return <div style={pageWrap}><div style={{ maxWidth: 720, margin: "60px auto" }}><div style={errBox}>{error}</div><Link to="/available-today/requests">← Back</Link></div></div>;
  if (!data) return null;

  const { request: r, matches } = data;
  const canCancel = r.status === "open";

  return (
    <div style={pageWrap} data-testid="request-detail-page">
      <div style={{ maxWidth: 1080, margin: "0 auto" }}>
        <p style={{ marginBottom: 12 }}>
          <Link to="/available-today/requests" data-testid="back-link" style={{ color: NAVY, textDecoration: "none", fontWeight: 700, letterSpacing: 1 }}>
            ← MY REQUESTS
          </Link>
        </p>

        <div style={{ background: "#fff", padding: "24px 26px", borderRadius: 14, border: "1px solid #e5e7ee", marginBottom: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: 20, flexWrap: "wrap" }}>
            <div>
              <div style={{ color: GOLD, letterSpacing: 3, fontSize: 12, fontWeight: 700 }}>LABOR REQUEST · {new Date(r.created_at).toLocaleString()}</div>
              <h1 style={h1}>{(r.skills || []).join(" · ").toUpperCase()}</h1>
              <div style={{ color: "#5b5e69", fontSize: 15 }}>
                {r.workers_needed} worker{r.workers_needed > 1 ? "s" : ""} · {r.city}{r.state ? `, ${r.state}` : ""} · ${r.hourly_offered_usd}/hr · {r.urgency.replace("_", " ")}
              </div>
              {r.description && <p style={{ color: NAVY, marginTop: 12, fontSize: 14 }} data-testid="request-description">{r.description}</p>}
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ background: STATUS_COLOR[r.status] || "#5b5e69", color: "#fff", padding: "8px 16px", borderRadius: 12, fontSize: 12, fontWeight: 700, letterSpacing: 2, display: "inline-block" }} data-testid="request-status-pill">
                {r.status.toUpperCase()}
              </div>
              <div style={{ fontSize: 13, color: "#5b5e69", marginTop: 8 }}>
                {r.workers_accepted}/{r.workers_needed} accepted
              </div>
              {canCancel && (
                <button onClick={cancel} disabled={cancelling}
                        data-testid="cancel-request-button"
                        style={{ marginTop: 12, background: "#FDECEC", color: RED, border: "none", padding: "8px 14px", borderRadius: 8, fontSize: 12, fontWeight: 700, letterSpacing: 1, cursor: "pointer" }}>
                  {cancelling ? "Cancelling…" : "CANCEL REQUEST"}
                </button>
              )}
            </div>
          </div>
        </div>

        <h2 style={h2}>MATCHES ({matches.length})</h2>
        {matches.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#5b5e69", background: "#fff", borderRadius: 12 }} data-testid="no-matches">
            No workers matched this request yet. If nobody accepts in 24h it will auto-expire — you can post a broader one.
          </div>
        ) : (
          <div style={grid} data-testid="match-list">
            {matches.map((m) => (
              <div key={m.id} style={cardStyle} data-testid={`match-card-${m.id}`}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                  <div>
                    <div style={{ fontFamily: "Bebas Neue, Arial, sans-serif", fontSize: 22, letterSpacing: 1, color: NAVY }} data-testid={`match-name-${m.id}`}>{m.worker_name}</div>
                    <ScoreBar score={m.score} />
                  </div>
                  <span style={{ background: STATE_COLOR[m.state] || "#5b5e69", color: "#fff", padding: "4px 10px", borderRadius: 10, fontSize: 10, fontWeight: 700, letterSpacing: 1.5 }} data-testid={`match-state-${m.id}`}>
                    {(m.state || "queued").replace("_", " ").toUpperCase()}
                  </span>
                </div>
                <div style={{ marginTop: 10, fontSize: 12, color: "#5b5e69" }}>
                  <div>Skills matched: <strong>{(m.factors?.overlapping_skills || []).join(", ") || "—"}</strong></div>
                  <div>Rate fit: {m.factors?.rate_fit}/15 · Availability: {m.factors?.availability}/20 · XP: {m.factors?.experience}/10 · Geo: {m.factors?.geography}/15</div>
                </div>
                {m.notified_sms_at && <NotifiedRow label="SMS sent" ts={m.notified_sms_at} />}
                {m.notified_email_at && <NotifiedRow label="Email sent" ts={m.notified_email_at} />}
                {m.notified_voice_at && <NotifiedRow label="ARIA voice call" ts={m.notified_voice_at} tone="voice" />}
                {m.responded_at && <NotifiedRow label={m.response_decision === "accept" ? "ACCEPTED" : "DECLINED"}
                                                ts={m.responded_at}
                                                tone={m.response_decision === "accept" ? "accept" : "decline"} />}
                {m.response_decision === "accept" && (m.worker_phone || m.worker_email) && (
                  <div style={{ marginTop: 12, padding: 10, background: NAVY, borderRadius: 10 }} data-testid={`match-contact-${m.id}`}>
                    {m.worker_phone && <div style={{ color: GOLD, fontSize: 13, fontWeight: 700 }}>📞 <a href={`tel:${m.worker_phone}`} style={{ color: GOLD, textDecoration: "none" }}>{m.worker_phone}</a></div>}
                    {m.worker_email && <div style={{ color: GOLD, fontSize: 12, marginTop: 2 }}>✉ <a href={`mailto:${m.worker_email}`} style={{ color: GOLD, textDecoration: "none" }}>{m.worker_email}</a></div>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const ScoreBar = ({ score }) => (
  <div style={{ marginTop: 6, marginBottom: 4, display: "flex", alignItems: "center", gap: 8 }}>
    <div style={{ flex: 1, height: 8, background: "#EFF1F5", borderRadius: 4, maxWidth: 140, overflow: "hidden" }}>
      <div style={{ width: `${score}%`, height: "100%", background: score >= 80 ? GREEN : score >= 60 ? GOLD : "#c7cad4", borderRadius: 4 }} />
    </div>
    <span style={{ fontSize: 13, fontWeight: 700, color: NAVY }}>{score}% match</span>
  </div>
);
const NotifiedRow = ({ label, ts, tone }) => (
  <div style={{
    marginTop: 6, padding: "6px 10px",
    background: tone === "accept" ? "#E8F5EE" : tone === "decline" ? "#FDECEC" : tone === "voice" ? "#FFF6E5" : "#F5F7FA",
    color: tone === "accept" ? GREEN : tone === "decline" ? RED : NAVY,
    borderRadius: 8, fontSize: 11, fontWeight: 700, letterSpacing: 1,
    display: "flex", justifyContent: "space-between",
  }}>
    <span>{label.toUpperCase()}</span>
    <span style={{ opacity: 0.7 }}>{new Date(ts).toLocaleTimeString()}</span>
  </div>
);

const pageWrap = { background: "#F8F9FA", minHeight: "100vh", padding: "40px 20px" };
const h1 = { fontFamily: "Bebas Neue, Arial, sans-serif", letterSpacing: 2.5, fontSize: 40, color: NAVY, margin: "6px 0 10px" };
const h2 = { fontFamily: "Bebas Neue, Arial, sans-serif", letterSpacing: 2, fontSize: 22, color: NAVY, margin: "0 0 14px" };
const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 14 };
const cardStyle = { background: "#fff", padding: "16px 18px", borderRadius: 14, border: "1px solid #e5e7ee", boxShadow: "0 8px 16px -8px rgba(10,22,40,0.08)" };
const errBox = { color: RED, background: "#FDECEC", padding: "12px 14px", borderRadius: 10, marginBottom: 14, fontSize: 14 };
